import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { IpcChannel, IpcV2Service } from './ipc-v2.service';

export interface LogEntry {
  level: 'info' | 'error' | 'warn' | 'debug' | 'testing' | 'log';
  message: string;
  timestamp: string;
  source: 'FRONTEND' | 'BACKEND';
}

@Injectable({
  providedIn: 'root',
})
export class LogService implements OnDestroy {
  private logSubject = new Subject<LogEntry>();
  private ipcSub?: Subscription;

  /** Subscribe to receive live log entries. */
  log$ = this.logSubject.asObservable();

  constructor(private ipcV2: IpcV2Service) {
    // Bridge: forward Electron LIVE_LOG_ENTRY events to our subject
    if (this.ipcV2.isElectron()) {
      this.ipcSub = this.ipcV2
        .on<LogEntry>(IpcChannel.LIVE_LOG_ENTRY as any)
        .subscribe({
          next: (entry) => this.logSubject.next(entry),
          error: (err) => console.error('LogService IPC error:', err),
        });
    }
  }

  /**
   * Log a message from the frontend.
   * Publishes to live subscribers AND sends to Electron backend for file logging.
   */
  log(level: LogEntry['level'], message: string): void {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      source: 'FRONTEND',
    };

    // Publish to any live subscribers
    this.logSubject.next(entry);

    // Persist to backend log file via IPC
    if (this.ipcV2.isElectron()) {
      this.ipcV2.send(IpcChannel.SAVE_FRONTEND_LOG, { level, message } as any);
    }
  }

  info(message: string) {
    this.log('info', message);
  }
  error(message: string) {
    this.log('error', message);
  }
  warn(message: string) {
    this.log('warn', message);
  }
  debug(message: string) {
    this.log('debug', message);
  }

  ngOnDestroy(): void {
    this.ipcSub?.unsubscribe();
    this.logSubject.complete();
  }
}
